import type { ReactNode } from 'react';
import type { StaffClaims } from '@macksons/web-client';
import { logoUrl, markUrl } from '../brand';
import { shortId, type OperatorScope } from '../auth/operator-scope';
import { useT } from '../hooks';
import { AccountMenu } from './AccountMenu';
import { ConnectivityBadge, OfflineBanner } from './ConnectivityStatus';
import { PrimaryNav } from './PrimaryNav';
import { UpdatePrompt } from './UpdatePrompt';

/**
 * Signed-in layout: skip link, a compact (~56px) app bar (mark + outlet name left;
 * connectivity pill + account menu right), a persistent offline banner, main content, and
 * the primary navigation as a bottom tab bar (phones) or a left side rail (portrait
 * tablet). The side rail and bottom bar are both rendered; CSS shows exactly one.
 */
export function Shell({ claims, scope, children }: { claims: StaffClaims; scope: OperatorScope | undefined; children: ReactNode }) {
  const t = useT();
  const outletName = scope?.outletId ? t('operator.scope.outletOption', { short: shortId(scope.outletId) }) : t('operator.appTitle');
  return (
    <div className="app has-rail">
      <a className="skip-link" href="#main">
        {t('operator.skipToContent')}
      </a>
      <header className="appbar">
        <div className="appbar__brand">
          <img className="appbar__mark" src={markUrl} alt={t('operator.appTitle')} />
          <span className="appbar__outlet" data-testid="outlet">
            {outletName}
          </span>
        </div>
        <div className="appbar__right">
          <ConnectivityBadge />
          <AccountMenu claims={claims} scope={scope} />
        </div>
      </header>
      <OfflineBanner />
      <UpdatePrompt />
      <PrimaryNav variant="rail" />
      <main id="main" tabIndex={-1} className="main">
        {children}
      </main>
      <PrimaryNav variant="bottom" />
    </div>
  );
}

/** Centred single-card layout for sign-in and status pages; optionally with the full logo. */
export function CenteredPage({ children, logo = false }: { children: ReactNode; logo?: boolean }) {
  const t = useT();
  return (
    <main id="main" className="centered">
      {logo && <img className="centered__logo" src={logoUrl} alt={t('operator.appTitle')} />}
      <div className="card">{children}</div>
    </main>
  );
}
